import { setPlatformOptions } from '@one/web-components/components';
import {
    STENCIL_DEFERRED_INTERACTION_COMPONENTS,
    STENCIL_DRAIN_SETTLE_PASSES,
    STENCIL_INTERACTION_EVENTS,
    STENCIL_MAX_DRAIN_PASSES,
    STENCIL_ROUTE_TRANSITION_TIMEOUT_MS,
} from './stencil-render-scheduler.constants';
import type { StencilSchedulerCompletion } from './stencil-render-scheduler.model';

type SchedulerMode = 'native' | 'preparing' | 'atomic';

let mode: SchedulerMode = 'native';
let transitionVersion = 0;
let pendingCallbacks = 0;
let contentReady = false;
let settleVersion = 0;
let transitionTimeout: ReturnType<typeof globalThis.setTimeout> | undefined;
let onSchedulerComplete: (completion: StencilSchedulerCompletion) => void;
let interactionActive = false;
let deferredScheduled = false;
let postDeferredDrain: (() => void) | undefined;
const deferredCallbacks: Array<() => void> = [];

const nativeRaf = (callback: FrameRequestCallback): number => globalThis.requestAnimationFrame(callback);

const clearTransitionTimeout = () => {
    if (transitionTimeout !== undefined) {
        globalThis.clearTimeout(transitionTimeout);
        transitionTimeout = undefined;
    }
};

const completeTransition = (version: number, completion: StencilSchedulerCompletion) => {
    if (version !== transitionVersion || mode !== 'atomic') {
        return;
    }

    mode = 'native';
    contentReady = false;
    settleVersion++;
    clearTransitionTimeout();
    onSchedulerComplete(completion);
};

const scheduleSettledCheck = () => {
    if (mode !== 'atomic' || !contentReady || pendingCallbacks > 0) {
        return;
    }

    const transition = transitionVersion;
    const settle = ++settleVersion;

    nativeRaf(() => {
        nativeRaf(() => {
            if (
                transition === transitionVersion &&
                settle === settleVersion &&
                mode === 'atomic' &&
                contentReady &&
                pendingCallbacks === 0
            ) {
                completeTransition(transition, 'settled');
            }
        });
    });
};

// A user interaction lives inside a single task, so a task-boundary timeout closes the window.
const isDeferredInteraction = (event: Event): boolean =>
    event
        .composedPath()
        .some(
            (eventTarget) =>
                eventTarget instanceof Element &&
                STENCIL_DEFERRED_INTERACTION_COMPONENTS.includes(eventTarget.localName)
        );

const beginInteraction = (event: Event) => {
    if (interactionActive || !isDeferredInteraction(event)) {
        return;
    }
    interactionActive = true;
    globalThis.setTimeout(() => {
        interactionActive = false;
    }, 0);
};

const listenForInteractions = () => {
    STENCIL_INTERACTION_EVENTS.forEach((eventType) =>
        globalThis.addEventListener(eventType, beginInteraction, { capture: true, passive: true })
    );
};

// Runs the whole cascade inside one task: each pass yields to the microtask queue so Stencil's
// promise continuations can enqueue the next level, which keeps the result to a single paint.
const drainDeferredCallbacks = async () => {
    let settledPasses = 0;

    for (let pass = 0; pass < STENCIL_MAX_DRAIN_PASSES; pass++) {
        if (deferredCallbacks.length === 0) {
            settledPasses++;
            if (settledPasses > STENCIL_DRAIN_SETTLE_PASSES) {
                return;
            }
        } else {
            settledPasses = 0;
            deferredCallbacks.splice(0).forEach((runCallback) => runCallback());
        }

        await Promise.resolve();
    }
};

const scheduleDeferredDrain = () => {
    if (deferredScheduled || postDeferredDrain === undefined) {
        return;
    }
    deferredScheduled = true;
    postDeferredDrain();
};

const handleDeferredDrainMessage = () => {
    void drainDeferredCallbacks().then(() => {
        deferredScheduled = false;
        if (deferredCallbacks.length > 0) {
            scheduleDeferredDrain();
        }
    });
};

// MessageChannel is the cheapest task hop in browsers. Environments without it (jsdom) fall back
// to a timeout, which is a task boundary all the same.
const createDeferredDrainPoster = (): (() => void) => {
    if (typeof MessageChannel === 'undefined') {
        return () => {
            globalThis.setTimeout(handleDeferredDrainMessage, 0);
        };
    }

    const channel = new MessageChannel();
    channel.port1.onmessage = handleDeferredDrainMessage;
    return () => channel.port2.postMessage(0);
};

const scheduleStencilFrame = (callback: FrameRequestCallback): number => {
    pendingCallbacks++;
    settleVersion++;

    const runCallback = () => {
        try {
            callback(performance.now());
        } finally {
            pendingCallbacks--;
            scheduleSettledCheck();
        }
    };

    // Default: microtasks. Animation frames make ODSU spread a cascade of slotted components over
    // one frame per level, which is visible as progressive re-rendering.
    //
    // Exception: while an event inside an ODSU select is dispatching, a microtask would run
    // *between* listeners and mutate the tree mid-propagation. The select defers its close decision
    // to the next frame, so one task hop keeps option selection and closing in the expected order.
    // Other interactions stay in microtasks so newly mounted components cannot paint unhydrated.
    //
    // Route transitions always take the microtask path: they must stay atomic, and the hydration
    // events ODSU fires during them must not be mistaken for user interaction.
    if (mode === 'atomic' || !interactionActive) {
        queueMicrotask(runCallback);
    } else {
        deferredCallbacks.push(runCallback);
        scheduleDeferredDrain();
    }

    // setPlatformOptions has no cancellation counterpart, so this handle is intentionally unused.
    return 0;
};

const startAtomicTransition = () => {
    clearTransitionTimeout();
    transitionVersion++;
    contentReady = false;
    settleVersion++;

    const version = transitionVersion;
    transitionTimeout = globalThis.setTimeout(
        () => completeTransition(version, 'timeout'),
        STENCIL_ROUTE_TRANSITION_TIMEOUT_MS
    );
    return version;
};

const waitUntilNextPaint = () =>
    new Promise<void>((resolve) => {
        nativeRaf(() => globalThis.setTimeout(resolve, 0));
    });

export const initializeStencilScheduler = (
    handleSchedulerComplete: (completion: StencilSchedulerCompletion) => void
) => {
    onSchedulerComplete = handleSchedulerComplete;
    postDeferredDrain = createDeferredDrainPoster();
    listenForInteractions();
    setPlatformOptions({ raf: scheduleStencilFrame });

    startAtomicTransition();
    mode = 'atomic';
};

export const prepareAtomicStencilWork = async () => {
    startAtomicTransition();
    mode = 'preparing';
    const version = transitionVersion;

    await waitUntilNextPaint();

    if (version === transitionVersion) {
        mode = 'atomic';
    }
};

export const markStencilContentReady = () => {
    if (mode !== 'atomic') {
        return;
    }
    contentReady = true;
    settleVersion++;
    scheduleSettledCheck();
};

export const markStencilContentPending = () => {
    if (mode !== 'atomic') {
        return;
    }
    contentReady = false;
    settleVersion++;
};
